import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    TextField,
    Divider,
    Chip,
    Skeleton
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import PedalBikeIcon from '@mui/icons-material/PedalBike';
import BuildIcon from '@mui/icons-material/Build';
import InfoIcon from '@mui/icons-material/Info';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { httpClient } from '../../common/httpclient';
import { standaloneFieldSx } from '../../common/fieldStyles';
import Dropdown from '../dropdown/Dropdown';
import TextAreaWithCounter from '../input/TextAreaWithCounter';
// `DialogForm` — DESIGN-SYSTEM.md §5.6 "Center modal" (scrim + box 20px +
// header ícone-badge/título/descrição + X + corpo rolável + rodapé com ações
// à direita). Este modal usava um `Dialog`/`DialogTitle`/`DialogContent`/
// `DialogActions` cru do MUI (header com gradiente roxo, botões com sx
// manual) — fora do padrão do resto do app, incl. do modal irmão "Novo
// cliente" (`component/NewCustomer.js`) aberto no mesmo fluxo
// (`service_order/selectorClient.js`, Nova Ordem/Novo Orçamento). Trocado
// para `DialogForm` (`maxWidth="md"` — grid de até 4 colunas não cabe no
// `sm` padrão) mantendo 100% do formik/validação/endpoint.
import DialogForm from '../dialog/DialogForm';

export default function ModalAdd({ open, onClose, customerId, customerName, onSuccess }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);
    const [selectedType, setSelectedType] = useState('');

    // Schema de validação para o formulário de veículo
    const vehicleValidationSchema = Yup.object({
        type: Yup.string().required('Tipo é obrigatório'),
        manufacturer_id: Yup.string().required('Fabricante é obrigatório'),
        model: Yup.string().required('Modelo é obrigatório'),
        fab_year: Yup.number()
            .required('Ano de fabricação é obrigatório')
            .min(1900, 'Ano de fabricação inválido')
            .max(new Date().getFullYear() + 1, 'Ano de fabricação inválido'),
        mod_year: Yup.number()
            .required('Ano do modelo é obrigatório')
            .min(1900, 'Ano do modelo inválido')
            .max(new Date().getFullYear() + 1, 'Ano do modelo inválido'),
        license_plate: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Placa é obrigatória'),
                otherwise: (schema) => schema.notRequired()
            }),
        chassi: Yup.string()
            .when('type', {
                is: (type) => type !== 'bicycle',
                then: (schema) => schema.required('Chassi é obrigatório').length(17, 'Chassi deve ter 17 caracteres'),
                otherwise: (schema) => schema.required('SN (Serial Number) é obrigatório')
            }),
        color: Yup.string().required('Cor é obrigatória'),
        kilometer: Yup.number()
            .required('Quilometragem é obrigatória')
            .min(0, 'Quilometragem deve ser maior ou igual a 0'),
        observation: Yup.string().max(500, 'Observação deve ter no máximo 500 caracteres')
    });

    // Formik para o formulário de veículo
    const vehicleFormik = useFormik({
        initialValues: {
            type: '',
            manufacturer_id: '',
            model: '',
            fab_year: '',
            mod_year: '',
            license_plate: '',
            chassi: '',
            color: '',
            kilometer: '',
            observation: ''
        },
        validationSchema: vehicleValidationSchema,
        onSubmit: async (values) => {
            try {
                const vehicleData = {
                    ...values,
                    customer_id: customerId
                };

                const response = await httpClient.post('/vehicles', vehicleData);
                if (response.status === 201 || response.status === 200) {
                    // Retorna o veículo criado para o callback
                    onSuccess(response.data);
                    handleClose();
                } else {
                    console.error('Erro ao criar veículo:', response.data);
                }
            } catch (error) {
                console.error('Error creating vehicle:', error);
            }
        },
    });

    const fetchManufacturers = async (type) => {
        try {
            setLoadingManufacturers(true);
            const response = await httpClient.get(`/vehicles/manufacturers/${type}`);

            if (Array.isArray(response.data)) {
                const validManufacturers = response.data.filter(item =>
                    item && typeof item === 'object' && item.id && item.name
                );
                setManufacturers(validManufacturers);
            } else {
                setManufacturers([]);
            }
        } catch (error) {
            console.error('Error fetching manufacturers:', error);
            setManufacturers([]);
        } finally {
            setLoadingManufacturers(false);
        }
    };

    const handleChangeType = (value) => {
        vehicleFormik.setFieldValue('type', value);
        vehicleFormik.setFieldValue('manufacturer_id', '');
        setManufacturers([]);
        setLoadingManufacturers(false);
        setSelectedType(value);
    };

    const handleChangeManufacturer = (value) => {
        vehicleFormik.setFieldValue('manufacturer_id', value);
    };

    const handleLicensePlateChange = (event) => {
        const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (value.length > 3) {
            const formattedValue = value.slice(0, 3) + '-' + value.slice(3);
            vehicleFormik.setFieldValue('license_plate', formattedValue);
        } else {
            vehicleFormik.setFieldValue('license_plate', value);
        }
    };

    const handleClose = () => {
        vehicleFormik.resetForm();
        setManufacturers([]);
        setSelectedType('');
        onClose();
    };

    // Efeito para carregar fabricantes quando o tipo muda
    useEffect(() => {
        if (vehicleFormik.values.type && !loadingManufacturers) {
            fetchManufacturers(vehicleFormik.values.type);
        }
    }, [vehicleFormik.values.type]);

    // Título de seção interna (`.field-card`-adjacent — só um rótulo com
    // ícone acima de um grupo de campos, não um accordion de verdade).
    const SectionHeading = ({ icon: Icon, children }) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Icon color="primary" fontSize="small" />
            <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>
                {children}
            </Typography>
        </Stack>
    );

    return (
        <DialogForm
            open={open}
            onClose={handleClose}
            onSubmit={vehicleFormik.handleSubmit}
            title="Cadastrar Novo Veículo"
            subtitle={customerName ? `Cliente: ${customerName}` : 'Preencha os campos abaixo para cadastrar o veículo.'}
            iconType="info"
            maxWidth="md"
            confirmButtonText="Salvar Veículo"
        >
            {/* Informações do Veículo */}
            <Box>
                <SectionHeading icon={BuildIcon}>Informações do Veículo</SectionHeading>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* First Row */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {/* Vehicle Type */}
                        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                            <Box sx={{ position: 'relative' }}>
                                <Dropdown
                                    label="Tipo de Veículo"
                                    labelVariant="static-micro"
                                    selectedItem={vehicleFormik.values.type}
                                    handleChange={handleChangeType}
                                    data={[
                                        { id: 'car', name: 'CARRO' },
                                        { id: 'motorcycle', name: 'MOTO' },
                                        { id: 'bicycle', name: 'BICICLETA' }
                                    ]}
                                    error={vehicleFormik.touched.type && Boolean(vehicleFormik.errors.type)}
                                    helperText={vehicleFormik.touched.type && vehicleFormik.errors.type}
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                />
                                {vehicleFormik.values.type && (
                                    <Chip
                                        icon={
                                            vehicleFormik.values.type === 'car' ? <DirectionsCarIcon /> :
                                            vehicleFormik.values.type === 'motorcycle' ? <TwoWheelerIcon /> :
                                            <PedalBikeIcon />
                                        }
                                        label={
                                            vehicleFormik.values.type === 'car' ? 'Carro' :
                                            vehicleFormik.values.type === 'motorcycle' ? 'Moto' :
                                            'Bicicleta'
                                        }
                                        color="primary"
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: 8,
                                            zIndex: 1
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Manufacturer */}
                        <Box sx={{ flex: '2 1 300px', minWidth: 0 }}>
                            {loadingManufacturers ? (
                                <Box>
                                    {/* Mesmo label micro/altura do `Dropdown` static-micro para não "pular" ao carregar */}
                                    <Typography sx={{ mb: '6px', fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                                        Fabricante
                                    </Typography>
                                    <Skeleton variant="rectangular" height={52} sx={{ borderRadius: '12px' }} />
                                </Box>
                            ) : (
                                <Dropdown
                                    label="Fabricante"
                                    labelVariant="static-micro"
                                    selectedItem={vehicleFormik.values.manufacturer_id}
                                    handleChange={handleChangeManufacturer}
                                    data={manufacturers || []}
                                    disabled={!vehicleFormik.values.type}
                                    error={vehicleFormik.touched.manufacturer_id && Boolean(vehicleFormik.errors.manufacturer_id)}
                                    helperText={
                                        vehicleFormik.touched.manufacturer_id && vehicleFormik.errors.manufacturer_id
                                            ? vehicleFormik.errors.manufacturer_id
                                            : loadingManufacturers
                                                ? 'Carregando fabricantes...'
                                                : ''
                                    }
                                    variant="outlined"
                                    size="medium"
                                    sx={standaloneFieldSx}
                                />
                            )}
                        </Box>

                        {/* Model — `pt` compensa o label estático (~22px) dos Dropdowns da linha, alinhando o topo das caixas */}
                        <Box sx={{ flex: '1 1 200px', minWidth: 0, pt: '22px' }}>
                            <TextField
                                fullWidth
                                label="Modelo"
                                value={vehicleFormik.values.model}
                                onChange={vehicleFormik.handleChange}
                                name="model"
                                disabled={!vehicleFormik.values.manufacturer_id}
                                error={vehicleFormik.touched.model && Boolean(vehicleFormik.errors.model)}
                                helperText={vehicleFormik.touched.model && vehicleFormik.errors.model}
                                variant="outlined"
                                sx={[standaloneFieldSx, {
                                    // Ícone à esquerda: label (sempre encolhido) e texto começam depois dele
                                    '& .MuiInputLabel-root.MuiInputLabel-shrink': { transform: 'translate(46px, 8px) scale(1)' },
                                    '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': { paddingLeft: 0 },
                                }]}
                                InputProps={{
                                    startAdornment:
                                        vehicleFormik.values.type === 'car' ? <DirectionsCarIcon sx={{ mr: 1, color: 'primary.main' }} /> :
                                        vehicleFormik.values.type === 'motorcycle' ? <TwoWheelerIcon sx={{ mr: 1, color: 'primary.main' }} /> :
                                        <PedalBikeIcon sx={{ mr: 1, color: 'primary.main' }} />
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Second Row */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {/* Years */}
                        <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
                            <TextField
                                fullWidth
                                label="Ano de Fabricação"
                                type="number"
                                value={vehicleFormik.values.fab_year}
                                onChange={vehicleFormik.handleChange}
                                name="fab_year"
                                error={vehicleFormik.touched.fab_year && Boolean(vehicleFormik.errors.fab_year)}
                                helperText={vehicleFormik.touched.fab_year && vehicleFormik.errors.fab_year}
                                variant="outlined"
                                sx={standaloneFieldSx}
                                inputProps={{
                                    min: 1900,
                                    max: new Date().getFullYear() + 1
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
                            <TextField
                                fullWidth
                                label="Ano do Modelo"
                                type="number"
                                value={vehicleFormik.values.mod_year}
                                onChange={vehicleFormik.handleChange}
                                name="mod_year"
                                error={vehicleFormik.touched.mod_year && Boolean(vehicleFormik.errors.mod_year)}
                                helperText={vehicleFormik.touched.mod_year && vehicleFormik.errors.mod_year}
                                variant="outlined"
                                sx={standaloneFieldSx}
                                inputProps={{
                                    min: 1900,
                                    max: new Date().getFullYear() + 1
                                }}
                            />
                        </Box>

                        {/* License Plate - Hidden for bicycles */}
                        {vehicleFormik.values.type !== 'bicycle' && (
                            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <TextField
                                    fullWidth
                                    label="Placa"
                                    value={vehicleFormik.values.license_plate}
                                    onChange={handleLicensePlateChange}
                                    name="license_plate"
                                    error={vehicleFormik.touched.license_plate && Boolean(vehicleFormik.errors.license_plate)}
                                    helperText={vehicleFormik.touched.license_plate && vehicleFormik.errors.license_plate}
                                    variant="outlined"
                                    sx={[standaloneFieldSx, {
                                        '& .MuiInputBase-input': {
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                            letterSpacing: 1
                                        }
                                    }]}
                                />
                            </Box>
                        )}

                        {/* Color */}
                        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                            <TextField
                                fullWidth
                                label="Cor"
                                value={vehicleFormik.values.color}
                                onChange={vehicleFormik.handleChange}
                                name="color"
                                error={vehicleFormik.touched.color && Boolean(vehicleFormik.errors.color)}
                                helperText={vehicleFormik.touched.color && vehicleFormik.errors.color}
                                variant="outlined"
                                sx={standaloneFieldSx}
                            />
                        </Box>

                        {/* Kilometer */}
                        <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
                            <TextField
                                fullWidth
                                label="Quilometragem"
                                type="number"
                                value={vehicleFormik.values.kilometer}
                                onChange={vehicleFormik.handleChange}
                                name="kilometer"
                                error={vehicleFormik.touched.kilometer && Boolean(vehicleFormik.errors.kilometer)}
                                helperText={vehicleFormik.touched.kilometer && vehicleFormik.errors.kilometer}
                                variant="outlined"
                                sx={standaloneFieldSx}
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                    </Box>

                    {/* Third Row - Chassi */}
                    <Box sx={{ width: '100%' }}>
                        <TextField
                            fullWidth
                            maxLength={17}
                            label={vehicleFormik.values.type === 'bicycle' ? 'SN (Serial Number)' : 'Número do Chassi'}
                            value={vehicleFormik.values.chassi}
                            onChange={vehicleFormik.handleChange}
                            name="chassi"
                            error={vehicleFormik.touched.chassi && Boolean(vehicleFormik.errors.chassi)}
                            helperText={vehicleFormik.touched.chassi && vehicleFormik.errors.chassi}
                            variant="outlined"
                            inputProps={{
                                maxLength: vehicleFormik.values.type === 'bicycle' ? undefined : 17,
                                style: {
                                    fontFamily: 'monospace',
                                    letterSpacing: 1
                                }
                            }}
                            sx={[standaloneFieldSx, {
                                '& .MuiInputBase-input': {
                                    textTransform: 'uppercase'
                                }
                            }]}
                        />
                    </Box>
                </Box>
            </Box>

            <Divider />

            {/* Observations Section */}
            <Box>
                <SectionHeading icon={InfoIcon}>Observações</SectionHeading>
                <TextAreaWithCounter
                    label="Observações (opcional)"
                    value={vehicleFormik.values.observation}
                    onChange={vehicleFormik.handleChange}
                    name="observation"
                    maxLength={500}
                    error={vehicleFormik.touched.observation && Boolean(vehicleFormik.errors.observation)}
                    helperText={vehicleFormik.touched.observation && vehicleFormik.errors.observation}
                    variant="outlined"
                    sx={standaloneFieldSx}
                />
            </Box>
        </DialogForm>
    );
}
